import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

import useRefValue from './useRefValue';

const dispatchStorageEvent = (key, newValue) => {
  window.dispatchEvent(new StorageEvent('storage', { key, newValue }));
};

export class WebStorage {
  constructor(storage) {
    this.storage = storage;
  }

  getItem(key) {
    const storageItem = this.storage.getItem(key);
    return storageItem && JSON.parse(storageItem);
  }

  setItem(key, value) {
    const stringifiedValue = JSON.stringify(value);
    this.storage.setItem(key, stringifiedValue);
    dispatchStorageEvent(key, stringifiedValue);
  }

  removeItem(key) {
    this.storage.removeItem(key);
    dispatchStorageEvent(key, null);
  }
}

const useLocalStorageSubscribe = (callback) => {
  window.addEventListener('storage', callback);
  return () => window.removeEventListener('storage', callback);
};

function useWebStorage(key, initialValue, storage = window.localStorage) {
  const { current: webStorage } = useRef(new WebStorage(storage));

  const getSnapshot = () => webStorage.getItem(key);

  const store = useSyncExternalStore(useLocalStorageSubscribe, getSnapshot);
  const state = store || initialValue;

  const memoKey = useRefValue(key);

  const setState = useCallback((nextState) => {
    try {
      if (nextState === undefined || nextState === null) {
        webStorage.removeItem(memoKey.current);
      } else {
        webStorage.setItem(memoKey.current, nextState);
      }
    } catch (e) {
      console.warn(e);
    }
  }, []);

  useEffect(() => {
    if (webStorage.getItem(key) === null && initialValue !== undefined) {
      webStorage.setItem(key, initialValue);
    }
  }, [key, initialValue]);

  return [state, setState];
}

export default useWebStorage;
